import type { PaketDuzenlemesi } from './paket-duzenleme';
/**
 * Hızlı teklif: ekran tipleri (`tipler.ts`) ile sunucu gövdeleri (`api/src/modules/hizli-teklif/**.dto.ts`) arasındaki
 * TEK çeviri yeri. Saf işlevlerdir (ağ yok, DOM yok); `istemci.ts` bunları çağırır, API tarafındaki
 * `hizli-teklif-sozlesme.spec.ts` bunların çıktısını gerçek DTO doğrulamasından ve gerçek servislerden geçirir.
 * Ekranla sunucu ayrışırsa o test kırılır; çeviri başka yere dağıtılmaz.
 */
import { manuelAnahtar, manuelGeceCoz, tutarCoz } from './mantik';
import type {
  IptalKosulu,
  KahvaltiDurumu,
  ManuelOtel,
  MesajTonu,
  PaketKodu,
  ParaBirimi,
  SecimGrubu,
  SecimOtel,
  SerbestKalem,
  SurumKaydi,
  SurumOzeti,
  TeklifSecimi,
  TeklifTaslagi,
} from './tipler';

/** Ekran paket anahtarı -> katalog kodu. Sunucudaki `paket-katalogu.ts` ile aynı olmak zorunda (test kilitler). */
export const KATALOG_KODU: Readonly<Record<PaketKodu, string>> = {
  TRANSFER: 'OZEL-ARAC-TRANSFER',
  DUBAI_TURU: 'DUBAI-TAM-GUN',
  ABU_DABI_TURU: 'ABU-DABI-TAM-GUN',
  SAFARI_TURU: 'DUBAI-CHOL-SAFARI',
  HELIKOPTER_TURU: 'DUBAI-HELIKOPTER',
  GOKYUZUNDE_YEMEK: 'DUBAI-GOKYUZUNDE-YEMEK',
  OZEL_YAT_TURU: 'DUBAI-OZEL-YAT',
};

// ---- Sunucu gövdeleri (DTO aynası) ----------------------------------------------------------------------------

export interface SunucuOtelSecimi {
  readonly otel: string;
  readonly odaSecimi?: Readonly<Record<string, string>>;
  readonly fiyatIsiId?: string;
  readonly ekNot?: string;
  readonly satisToplamMinor?: number;
}

export interface SunucuManuelOtel {
  readonly id: string;
  readonly ad: string;
  readonly sehir: string;
  readonly odaAdi: string;
  readonly tutarTuru: 'TOPLAM' | 'GECELIK';
  readonly tutarMinor: number;
  readonly paraBirimi: ParaBirimi;
  readonly gece?: number;
  readonly iptalMetni?: string;
  readonly ekNot?: string;
}

export interface SunucuBolumSecimi {
  /** Katalogdan otel seçilmediyse (yalnız manuel oteller) yok; o zaman tarihler bölümde gönderilir. */
  readonly fiyatIsiId?: string;
  readonly girisTarihi?: string;
  readonly cikisTarihi?: string;
  readonly oteller?: readonly SunucuOtelSecimi[];
  readonly manuelOteller?: readonly SunucuManuelOtel[];
  readonly secilenOtel?: string;
}

export interface SunucuSerbestKalem {
  readonly ad: string;
  readonly aciklama?: string;
  readonly tutarMinor: number;
  readonly paraBirimi: ParaBirimi;
  readonly birim: 'SABIT' | 'KISI_BASI' | 'GUN_BASI' | 'SAAT_BASI';
  readonly miktar: number;
  readonly kapsam: 'TUM' | 'SECENEK';
  readonly secenek?: string;
  readonly kdvNotu?: string;
}

export interface SunucuTeklifSecimi {
  readonly dil?: 'tr' | 'en';
  readonly paketDuzenlemeleri?: readonly PaketDuzenlemesi[];
  readonly bolumler: readonly SunucuBolumSecimi[];
  readonly geziPaketleri?: readonly string[];
  readonly ton?: MesajTonu;
  readonly geziEkNotlari?: Readonly<Record<string, string>>;
  readonly teklifNotu?: string;
  readonly aracSecimi?: 'DORT' | 'ALTI' | 'KARISIK';
  readonly serbestKalemler?: readonly SunucuSerbestKalem[];
  readonly kdvNotu?: string;
  readonly planlamaMetni?: string;
  readonly isleyisMetni?: string;
  readonly safariBebekKoltukSayisi?: number;
  readonly yat?: { readonly saat: number; readonly boyut?: string };
  /** Hiçbir bölümde katalog oteli yoksa oda dağılımı (fiyat işi olmadığı için). */
  readonly odalar?: readonly { readonly yetiskin: number; readonly cocukYaslari: readonly number[]; readonly cocukAylari?: readonly number[] }[];
}

export interface SunucuPdfIstegi extends SunucuTeklifSecimi {
  readonly musteriAdi?: string;
  readonly destinasyon?: string;
}

export interface SunucuPdfCevabi {
  readonly id: string;
  readonly indirmeAdresi: string;
  readonly dosyaAdi: string;
  readonly gecerlilikBitisi: string;
  readonly boyutBayt: number;
  readonly sayfa: number | null;
  readonly boyutAsildi: boolean;
  readonly mesaj: string;
}

export interface SunucuKayitSecenegi {
  readonly id: string;
  readonly etiket: string;
  readonly otelSlug: string;
  readonly otelNo?: string;
  readonly otelAdi: string;
  readonly odaTipleri: readonly string[];
  readonly pansiyon: string;
  readonly toplamMinor: number;
  readonly paraBirimi: ParaBirimi;
  readonly odaNolari?: readonly number[];
  readonly aramaNo?: string;
  /** Katalogda olmayan (manuel) otelde yok: fiyat satışçının yazdığıdır, süresi yoktur. */
  readonly quoteExpiresAt?: string;
  readonly iptal: IptalKosulu;
  readonly ekNot?: string;
  readonly manuel?: true;
  readonly gece?: number;
  readonly sehir?: string;
  readonly iptalMetni?: string;
}

export interface SunucuKayitIstegi {
  readonly musteriId: string;
  readonly girisTarihi: string;
  readonly cikisTarihi: string;
  readonly odalar: readonly { readonly yetiskin: number; readonly cocukYaslari: readonly number[]; readonly cocukAylari?: readonly number[] }[];
  readonly teklifNotu?: string;
  readonly aracSecimi?: 'DORT' | 'ALTI' | 'KARISIK';
  readonly uyruk?: string;
  readonly serbestKalemler?: readonly SunucuSerbestKalem[];
  readonly kdvNotu?: string;
  readonly planlamaMetni?: string;
  readonly isleyisMetni?: string;
  readonly safariBebekKoltukSayisi?: number;
  readonly yat?: { readonly saat: number; readonly boyut?: string };
  readonly gruplar: readonly { readonly anahtar: string; readonly etiket: string; readonly girisTarihi: string; readonly cikisTarihi: string; readonly secenekler: readonly SunucuKayitSecenegi[]; readonly secilenId?: string }[];
  readonly paketler?: readonly { readonly anahtar: string; readonly tur: 'GEZI' | 'TRANSFER'; readonly ad: string; readonly ozet?: string; readonly toplamMinor: number; readonly paraBirimi: ParaBirimi; readonly secili: boolean; readonly ekNot?: string }[];
  readonly fiyatAlindiAt: string;
  readonly mesaj?: { readonly metin: string; readonly ton: MesajTonu; readonly kanal: 'WHATSAPP' };
  readonly ekranTaslagi: TeklifTaslagi;
}

// ---- PDF ve mesaj ---------------------------------------------------------------------------------------------

const OTEL_NO = /^\d{7}$/;

function otelAnahtari(o: SecimOtel): string {
  const k = o.slug ?? o.otelNo;
  if (k === null) throw new Error('Seçili otelin ne slug ne numarası var');
  return k;
}

function manuelGovdesi(o: SecimOtel, notlarla: boolean): SunucuManuelOtel {
  const m = o.manuel as ManuelOtel;
  const tutar = tutarCoz(m.tutar);
  if (tutar === null) throw new Error('Manuel otelin tutarı geçersiz');
  return {
    id: m.id,
    ad: m.ad.trim(),
    sehir: m.sehir.trim(),
    odaAdi: m.odaAdi.trim(),
    tutarTuru: m.tutarTuru,
    tutarMinor: tutar,
    paraBirimi: m.paraBirimi,
    ...(manuelGeceCoz(m.gece).deger === null ? {} : { gece: manuelGeceCoz(m.gece).deger as number }),
    ...(m.iptalMetni.trim() === '' ? {} : { iptalMetni: m.iptalMetni.trim() }),
    ...(notlarla && m.ekNot.trim() !== '' ? { ekNot: m.ekNot.trim() } : {}),
  };
}

function bolumGovdesi(g: SecimGrubu, coklu: boolean, notlarla: boolean): SunucuBolumSecimi {
  const katalog = g.secenekler.filter((o) => o.manuel === undefined);
  const manuel = g.secenekler.filter((o) => o.manuel !== undefined);
  const ilk = g.secenekler[0];
  const secilen = coklu && ilk !== undefined ? { secilenOtel: ilk.manuel === undefined ? otelAnahtari(ilk) : manuelAnahtar(ilk.manuel.id) } : {};
  const manuelGovde = manuel.length === 0 ? {} : { manuelOteller: manuel.map((o) => manuelGovdesi(o, notlarla)) };
  if (katalog.length === 0) return { girisTarihi: g.girisTarihi, cikisTarihi: g.cikisTarihi, ...manuelGovde, ...secilen };
  if (g.fiyatIsiId === null) throw new Error('Bölümün fiyat işi bilinmiyor; fiyatları yeniden çekin');
  const bolumIsi = g.fiyatIsiId;
  const oteller = katalog.map((o): SunucuOtelSecimi => {
    const odaSecimi: Record<string, string> = {};
    for (const x of o.odalar) odaSecimi[x.grupAnahtari] = x.secenekNo;
    return { otel: otelAnahtari(o), odaSecimi, ...(notlarla && o.ekNot !== undefined ? { ekNot: o.ekNot } : {}), ...(o.satisToplamMinor === undefined ? {} : { satisToplamMinor: o.satisToplamMinor }), ...(o.fiyatIsiId !== null && o.fiyatIsiId !== bolumIsi ? { fiyatIsiId: o.fiyatIsiId } : {}) };
  });
  return { fiyatIsiId: bolumIsi, oteller, ...manuelGovde, ...secilen };
}

function kalemGovdesi(k: SerbestKalem): SunucuSerbestKalem {
  const tutar = tutarCoz(k.tutar);
  if (tutar === null) throw new Error('Serbest kalemin tutarı geçersiz');
  return {
    ad: k.ad.trim(),
    ...(k.aciklama.trim() === '' ? {} : { aciklama: k.aciklama.trim() }),
    tutarMinor: tutar,
    paraBirimi: k.paraBirimi,
    birim: k.birim,
    miktar: k.birim === 'SABIT' ? 1 : Number(k.miktar),
    kapsam: k.kapsam,
    ...(k.kapsam === 'SECENEK' && k.secenek !== null ? { secenek: k.secenek } : {}),
    ...(k.kdvNotu.trim() === '' ? {} : { kdvNotu: k.kdvNotu.trim() }),
  };
}

/** Hem mesaj hem PDF isteğinde giden ortak alanlar: kalemler, KDV notu, yat seçimi ve (katalog oteli yoksa) odalar. */
function ortakAlanlar(secim: TeklifSecimi): Pick<SunucuTeklifSecimi, 'serbestKalemler' | 'kdvNotu' | 'yat' | 'odalar' | 'safariBebekKoltukSayisi'> {
  const katalogVar = secim.gruplar.some((g) => g.secenekler.some((o) => o.manuel === undefined));
  return {
    ...(secim.serbestKalemler === undefined || secim.serbestKalemler.length === 0 ? {} : { serbestKalemler: secim.serbestKalemler.map(kalemGovdesi) }),
    ...(secim.kdvNotu === undefined || secim.kdvNotu.trim() === '' ? {} : { kdvNotu: secim.kdvNotu.trim() }),
    ...(secim.safariBebekKoltukSayisi === undefined ? {} : { safariBebekKoltukSayisi: secim.safariBebekKoltukSayisi }),
    ...(secim.yat === undefined ? {} : { yat: { saat: secim.yat.saat, ...(secim.yat.boyut === null ? {} : { boyut: secim.yat.boyut }) } }),
    ...(katalogVar ? {} : { odalar: secim.odalar }),
  };
}

/** Teklifteki paketlerin katalog kodları: tek bölümde seçeneklerin içindeki paketler, çoklu bölümde planın paketleri. */
export function geziKodlari(secim: TeklifSecimi): string[] {
  const coklu = secim.gruplar.length > 1;
  const paketler = coklu ? secim.planPaketleri : secim.gruplar.flatMap((g) => g.secenekler.flatMap((o) => o.paketler));
  return [...new Set(paketler.map((p) => KATALOG_KODU[p.kod]))];
}

export function mesajIstegiKur(secim: TeklifSecimi, ton: MesajTonu, dil: 'tr' | 'en' = 'tr'): SunucuTeklifSecimi {
  const coklu = secim.gruplar.length > 1;
  const gezi = geziKodlari(secim);
  return { ...(dil === 'en' ? { dil } : {}), ...(secim.paketDuzenlemeleri === undefined ? {} : { paketDuzenlemeleri: secim.paketDuzenlemeleri }), bolumler: secim.gruplar.map((g) => bolumGovdesi(g, coklu, false)), ...(gezi.length > 0 ? { geziPaketleri: gezi } : {}), ...(secim.aracSecimi === undefined ? {} : { aracSecimi: secim.aracSecimi }), ...ortakAlanlar(secim), ton };
}

export function pdfIstegiKur(secim: TeklifSecimi, ton: MesajTonu, dil: 'tr' | 'en' = 'tr'): SunucuPdfIstegi {
  // Satışçının serbest notları yalnız PDF isteğine girer; mesaj taslağı onları taşımaz (mesajIstegiKur notsuzdur).
  const coklu = secim.gruplar.length > 1;
  const gezi = geziKodlari(secim);
  const geziNotlari = Object.fromEntries(Object.entries(secim.geziEkNotlari ?? {}).filter(([, v]) => v !== undefined && v.trim() !== '').map(([k, v]) => [KATALOG_KODU[k as PaketKodu], v as string]));
  return {
    ...(secim.paketDuzenlemeleri === undefined ? {} : { paketDuzenlemeleri: secim.paketDuzenlemeleri }),
    ...(dil === 'en' ? { dil } : {}),
    bolumler: secim.gruplar.map((g) => bolumGovdesi(g, coklu, true)),
    ...(gezi.length > 0 ? { geziPaketleri: gezi } : {}),
    ...(secim.aracSecimi === undefined ? {} : { aracSecimi: secim.aracSecimi }),
    ...ortakAlanlar(secim),
    ...(secim.planlamaMetni === undefined || secim.planlamaMetni.trim() === '' ? {} : { planlamaMetni: secim.planlamaMetni.trim() }),
    ...(secim.isleyisMetni === undefined || secim.isleyisMetni.trim() === '' ? {} : { isleyisMetni: secim.isleyisMetni.trim() }),
    ton,
    ...(Object.keys(geziNotlari).length === 0 ? {} : { geziEkNotlari: geziNotlari }),
    ...(secim.teklifNotu === undefined ? {} : { teklifNotu: secim.teklifNotu }),
    musteriAdi: secim.musteriAdi,
    destinasyon: secim.destinasyon,
  };
}

const PDF_YOLU = '/api/v1/hizli-teklif/pdf/';

/** Sunucunun verdiği indirme adresi yalnız kendi PDF yoluna işaret edebilir (başka adrese istek atılmaz). */
export function pdfAdresiDogrula(adres: string): string {
  if (!adres.startsWith(PDF_YOLU) || !/^[a-f0-9]{32}$/.test(adres.slice(PDF_YOLU.length))) throw new Error('PDF indirme adresi beklenmedik');
  return adres;
}

// ---- Kayıt ----------------------------------------------------------------------------------------------------

const KAHVALTI_METNI: Readonly<Record<KahvaltiDurumu, string>> = {
  KAHVALTI_DAHIL: 'Kahvaltı dahil',
  YALNIZ_ODA: 'Yalnız oda',
  YARIM_PANSIYON: 'Yarım pansiyon',
  TAM_PANSIYON: 'Tam pansiyon',
  HER_SEY_DAHIL: 'Her şey dahil',
  BILINMIYOR: 'Pansiyon teyit edilir',
};

const kes = (m: string, n: number): string => (m.length <= n ? m : m.slice(0, n));

function iptalBirlestir(o: SecimOtel): IptalKosulu {
  const iadeler = o.odalar.map((x) => x.iptal.iadeEdilebilir);
  if (iadeler.some((x) => x === false)) return { iadeEdilebilir: false, sonTarih: null };
  if (iadeler.length > 0 && iadeler.every((x) => x === true)) {
    const tarihler = o.odalar.flatMap((x) => (x.iptal.sonTarih === null ? [] : [x.iptal.sonTarih])).sort((a, b) => Date.parse(a) - Date.parse(b));
    return { iadeEdilebilir: true, sonTarih: tarihler[0] ?? null };
  }
  return { iadeEdilebilir: null, sonTarih: null };
}

function secenekGovdesi(o: SecimOtel, id: string, sira: number): SunucuKayitSecenegi {
  if (o.manuel !== undefined) {
    const m = o.manuel;
    return {
      id,
      etiket: kes(`Seçenek ${String(sira)}: ${m.ad.trim()}`, 80),
      otelSlug: manuelAnahtar(m.id),
      otelAdi: kes(m.ad.trim(), 120),
      odaTipleri: [kes(m.odaAdi.trim(), 120)],
      pansiyon: '',
      toplamMinor: o.konaklamaMinor,
      paraBirimi: o.paraBirimi,
      iptal: { iadeEdilebilir: null, sonTarih: null },
      manuel: true,
      ...(manuelGeceCoz(m.gece).deger === null ? {} : { gece: manuelGeceCoz(m.gece).deger as number }),
      sehir: kes(m.sehir.trim(), 60),
      ...(m.iptalMetni.trim() === '' ? {} : { iptalMetni: kes(m.iptalMetni.trim(), 300) }),
      ...(m.ekNot.trim() === '' ? {} : { ekNot: m.ekNot.trim() }),
    };
  }
  const tipler = [...new Set(o.odalar.map((x) => kes(x.odaTipi, 120)))].slice(0, 8);
  const pansiyon = [...new Set(o.odalar.map((x) => KAHVALTI_METNI[x.kahvalti]))].join(' / ');
  const nolar = [...new Set(o.odalar.flatMap((x) => x.odaNolari))].sort((a, b) => a - b).slice(0, 8);
  const aramaNo = o.odalar[0]?.aramaNo;
  const no = o.otelNo ?? '';
  return {
    id,
    etiket: kes(`Seçenek ${String(sira)}: ${o.otelAdi}`, 80),
    otelSlug: o.slug ?? '',
    ...(OTEL_NO.test(no) ? { otelNo: no } : {}),
    otelAdi: kes(o.otelAdi, 120),
    odaTipleri: tipler.length > 0 ? tipler : ['Oda'],
    pansiyon: kes(pansiyon, 120),
    toplamMinor: o.konaklamaMinor,
    paraBirimi: o.paraBirimi,
    ...(nolar.length > 0 ? { odaNolari: nolar } : {}),
    ...(aramaNo !== undefined && aramaNo !== '' ? { aramaNo: kes(aramaNo, 80) } : {}),
    quoteExpiresAt: o.gecerlilikBitis,
    iptal: iptalBirlestir(o),
    ...(o.ekNot === undefined ? {} : { ekNot: o.ekNot }),
  };
}

export function kayitIstegiKur(secim: TeklifSecimi, taslak: TeklifTaslagi, mesaj: { readonly metin: string; readonly ton: MesajTonu } | null, simdi: Date): SunucuKayitIstegi {
  const coklu = secim.gruplar.length > 1;
  const gruplar = secim.gruplar.map((g) => {
    const secenekler = g.secenekler.map((o, i) => secenekGovdesi(o, `S${String(i + 1)}`, i + 1));
    return {
      anahtar: g.anahtar,
      etiket: kes(g.etiket, 80),
      girisTarihi: g.girisTarihi,
      cikisTarihi: g.cikisTarihi,
      secenekler,
      // Çoklu bölümde her bölümde tek seçenek vardır ve toplama o girer; tek bölümde en ucuz varsayılandır.
      ...(coklu && secenekler[0] !== undefined ? { secilenId: secenekler[0].id } : {}),
    };
  });
  const paketKaynagi = coklu
    ? secim.planPaketleri.map((p) => ({ ...p, para: secim.genelParaBirimi }))
    : (secim.gruplar[0]?.secenekler[0]?.paketler ?? []).map((p) => ({ ...p, para: secim.gruplar[0]?.secenekler[0]?.paraBirimi ?? null }));
  const paketler = paketKaynagi
    .filter((p) => p.tutarMinor > 0 && p.para !== null)
    .map((p) => ({ anahtar: p.kod, tur: p.kod === 'TRANSFER' ? ('TRANSFER' as const) : ('GEZI' as const), ad: kes(p.ad, 120), ozet: kes(p.kapsam, 300), toplamMinor: p.tutarMinor, paraBirimi: p.para as ParaBirimi, secili: true, ...(secim.geziEkNotlari?.[p.kod] === undefined ? {} : { ekNot: secim.geziEkNotlari[p.kod] as string }) }));
  const girisler = secim.gruplar.map((g) => g.girisTarihi).sort();
  const cikislar = secim.gruplar.map((g) => g.cikisTarihi).sort();
  return {
    musteriId: secim.musteriId,
    girisTarihi: girisler[0] ?? '',
    cikisTarihi: cikislar[cikislar.length - 1] ?? '',
    odalar: secim.odalar,
    ...(secim.teklifNotu === undefined ? {} : { teklifNotu: secim.teklifNotu }),
    ...(secim.aracSecimi === undefined ? {} : { aracSecimi: secim.aracSecimi }),
    ...(({ serbestKalemler, kdvNotu, yat, safariBebekKoltukSayisi }) => ({ ...(safariBebekKoltukSayisi === undefined ? {} : { safariBebekKoltukSayisi }), ...(serbestKalemler === undefined ? {} : { serbestKalemler }), ...(kdvNotu === undefined ? {} : { kdvNotu }), ...(yat === undefined ? {} : { yat }) }))(ortakAlanlar(secim)),
    ...(secim.planlamaMetni === undefined || secim.planlamaMetni.trim() === '' ? {} : { planlamaMetni: secim.planlamaMetni.trim() }),
    ...(secim.isleyisMetni === undefined || secim.isleyisMetni.trim() === '' ? {} : { isleyisMetni: secim.isleyisMetni.trim() }),
    ...(/^[A-Z]{2}$/.test(secim.uyruk) ? { uyruk: secim.uyruk } : {}),
    gruplar,
    ...(paketler.length > 0 ? { paketler } : {}),
    fiyatAlindiAt: simdi.toISOString(),
    ...(mesaj !== null && mesaj.metin.trim() !== '' ? { mesaj: { metin: kes(mesaj.metin, 4000), ton: mesaj.ton, kanal: 'WHATSAPP' as const } } : {}),
    ekranTaslagi: taslak,
  };
}

// ---- Kayıt cevapları ------------------------------------------------------------------------------------------

interface SunucuIcerik {
  readonly ozet?: { readonly otelSayisi?: number; readonly secenekSayisi?: number };
  readonly ekranTaslagi?: TeklifTaslagi | null;
}

export interface SunucuSurumGorunumu {
  readonly teklifId: string;
  readonly surumNo: number;
  readonly surumId: string;
  readonly olusturuldu: string;
  readonly icerik: SunucuIcerik | null;
}

export function surumOzetiCevir(g: SunucuSurumGorunumu): SurumOzeti {
  return { id: g.surumId, teklifId: g.teklifId, surumNo: g.surumNo, kayitZamani: g.olusturuldu, otelSayisi: g.icerik?.ozet?.otelSayisi ?? 0, secenekSayisi: g.icerik?.ozet?.secenekSayisi ?? 0 };
}

export function surumKaydiCevir(g: SunucuSurumGorunumu): SurumKaydi {
  const taslak = g.icerik?.ekranTaslagi;
  if (taslak === undefined || taslak === null) throw new Error('Bu sürümde ekran taslağı yok; salt okunur açılamaz');
  return { ...surumOzetiCevir(g), taslak };
}
